import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Banknote, CheckCircle2, Download, Send, XCircle } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BATCH_STATUS, fmtDate, fmtDateTime, fmtMoney } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { DemoBanner, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function ReconcileDialog({ batch, instr, open, onClose, onDone }) {
  const [paid, setPaid] = useState("");
  const [explain, setExplain] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/finanzas/payments/batches/${batch.id}/reconcile/${instr.id}`, {
        paid_amount: paid ? Number(paid) : undefined, explain_difference: explain || undefined,
      });
      toast.success("Instrucción conciliada");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="reconcile-dialog">
        <DialogHeader><DialogTitle>Conciliar {instr?.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Esperado: <Money value={instr?.amount} />. Si el banco confirma un importe distinto, explica la diferencia (devolución, comisión, pago parcial).</p>
          <div><Label>Importe pagado (€) — vacío = esperado</Label><Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} data-testid="reconcile-paid" /></div>
          <div><Label>Explicación de la diferencia</Label><Textarea value={explain} onChange={(e) => setExplain(e.target.value)} data-testid="reconcile-explain" /></div>
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="reconcile-confirm">{busy ? "Conciliando…" : "Conciliar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BatchDetail({ bid }) {
  const { map } = useUsers();
  const qc = useQueryClient();
  const [simulateEvent, setSimulateEvent] = useState("enviada");
  const [reconcile, setReconcile] = useState(null);
  const { data, refetch } = useQuery({
    queryKey: ["fin-batch", bid],
    queryFn: async () => (await api.get(`/finanzas/payments/batches/${bid}`)).data,
  });
  if (!data) return null;
  const { batch, confirmations, approvals } = data;

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/finanzas/payments/batches/${batch.id}/action`, { action, ...extra });
      toast.success("Acción registrada");
      refetch();
      qc.invalidateQueries({ queryKey: ["fin-batches"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="ts-card mt-4 p-5" data-testid={`batch-detail-${batch.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-display text-base font-semibold">{batch.batch_number}</p>
          <Chip map={BATCH_STATUS} value={batch.status} testid={`batch-status-${batch.id}`} />
          <ToneChip label="payment_mock" tone="muted" />
        </div>
        <Money value={batch.total_amount} className="text-xl font-bold" />
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {batch.invoice_count} instrucciones · fecha de pago {fmtDate(batch.payment_date)} · cuenta {batch.bank_account} ·
        creada por {userName(map, batch.created_by)}{batch.approved_by ? ` · aprobada por ${userName(map, batch.approved_by)}` : ""}
        {batch.export_hash ? ` · hash ${batch.export_hash}` : ""}
      </p>
      {batch.validation_errors?.length > 0 && (
        <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="batch-validation-errors">
          Excluidas en validación: {batch.validation_errors.join(" · ")}
        </div>
      )}

      <Table>
        <TableHeader><TableRow><TableHead>Factura</TableHead><TableHead>Proveedor</TableHead><TableHead>IBAN</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Importe</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {batch.instructions.map((i) => (
            <TableRow key={i.id} data-testid={`batch-instruction-${i.id}`}>
              <TableCell className="font-mono text-xs">{i.invoice_number}</TableCell>
              <TableCell className="text-sm">{i.supplier_name}</TableCell>
              <TableCell className="font-mono text-xs">····{i.iban_last4}</TableCell>
              <TableCell><ToneChip label={i.status} tone={i.status === "ejecutada" ? "success" : i.status === "rechazada" ? "danger" : "info"} /></TableCell>
              <TableCell className="text-right"><Money value={i.amount} /></TableCell>
              <TableCell className="text-right">
                {batch.status === "pendiente_conciliacion" && i.status === "ejecutada" && (
                  <Button size="sm" variant="outline" onClick={() => setReconcile(i)} data-testid={`reconcile-${i.id}`}>Conciliar</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {confirmations.length > 0 && (
        <div className="mt-3" data-testid="batch-confirmations">
          <p className="mb-1 text-xs font-semibold text-slate-500">Confirmaciones bancarias (mock)</p>
          {confirmations.map((c) => (
            <p key={c.id} className="text-xs text-slate-500" data-testid={`confirmation-${c.id}`}>
              {c.reference} · esperado {fmtMoney(c.expected_amount)} / pagado {fmtMoney(c.paid_amount)} · {fmtDateTime(c.date)} · origen {c.origin}
            </p>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="batch-actions">
        {["borrador", "en_validacion", "con_errores"].includes(batch.status) && (
          <Button size="sm" onClick={() => act("solicitar_aprobacion")} data-testid={`batch-approve-req-${batch.id}`}><Send className="mr-1 h-3.5 w-3.5" /> Solicitar aprobación</Button>
        )}
        {batch.status === "aprobada" && (
          <Button size="sm" onClick={() => act("exportar")} data-testid={`batch-export-${batch.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Exportar archivo demo</Button>
        )}
        {["exportada", "enviada", "aceptada", "parcialmente_rechazada"].includes(batch.status) && (
          <div className="flex items-center gap-1.5">
            <Select value={simulateEvent} onValueChange={setSimulateEvent}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid={`batch-sim-event-${batch.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="enviada">Banco: enviada</SelectItem>
                <SelectItem value="aceptada">Banco: aceptada</SelectItem>
                <SelectItem value="rechazo_parcial">Banco: rechazo parcial</SelectItem>
                <SelectItem value="ejecutada">Banco: ejecutada</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => act("simular", { event: simulateEvent })} data-testid={`batch-simulate-${batch.id}`}>Simular respuesta bancaria</Button>
          </div>
        )}
        {!["cancelada", "conciliada", "pendiente_conciliacion"].includes(batch.status) && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => act("cancelar")} data-testid={`batch-cancel-${batch.id}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Cancelar</Button>
        )}
        {batch.status === "conciliada" && <span className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Remesa conciliada</span>}
      </div>
      {approvals.length > 0 && (
        <p className="mt-2 text-xs text-slate-400">Última aprobación: {approvals[0].status} · {approvals[0].decision_comment || "—"}</p>
      )}
      {reconcile && <ReconcileDialog batch={batch} instr={reconcile} open onClose={() => setReconcile(null)} onDone={() => { setReconcile(null); refetch(); qc.invalidateQueries({ queryKey: ["fin-batches"] }); }} />}
    </div>
  );
}

export default function Remesas() {
  const { user } = useAuth();
  const canWrite = (user?.roles || []).some((r) => r !== "VIEWER");
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const { data: proposal, refetch: refetchProposal } = useQuery({
    queryKey: ["fin-proposal"],
    queryFn: async () => (await api.get("/finanzas/payments/proposal")).data,
  });
  const { data: batches } = useQuery({
    queryKey: ["fin-batches"],
    queryFn: async () => (await api.get("/finanzas/payments/batches")).data,
  });
  const openBatch = params.get("batch");

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const createBatch = async () => {
    try {
      const r = await api.post("/finanzas/payments/batches", { invoice_ids: selected });
      toast.success(`Remesa ${r.data.batch_number} creada en validación (${fmtMoney(r.data.total_amount)})`);
      setSelected([]);
      refetchProposal();
      qc.invalidateQueries({ queryKey: ["fin-batches"] });
      setParams({ batch: r.data.id });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="remesas-page">
      <PageHeader title="Remesas y pagos" subtitle="Propuesta de pago con exclusiones explicadas, aprobación maker-checker y exportación demo. Ningún pago se ejecuta realmente." />
      <DemoBanner />

      <h2 className="ts-section-title mb-3 mt-6">Propuesta de pago</h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="ts-card overflow-hidden" data-testid="proposal-eligible">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <p className="font-display text-sm font-semibold">Elegibles ({proposal?.eligible?.length || 0})</p>
            {canWrite && (
              <Button size="sm" disabled={!selected.length} onClick={createBatch} data-testid="create-batch-button">
                <Banknote className="mr-1 h-3.5 w-3.5" /> Crear remesa ({selected.length})
              </Button>
            )}
          </div>
          <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
            {(proposal?.eligible || []).map((inv) => (
              <label key={inv.id} className="flex cursor-pointer items-center gap-3 px-5 py-2.5 hover:bg-slate-50" data-testid={`eligible-${inv.id}`}>
                {canWrite && <input type="checkbox" checked={selected.includes(inv.id)} onChange={() => toggle(inv.id)} />}
                <span className="flex-1 text-sm">{inv.invoice_number} — {inv.supplier_name}</span>
                <Money value={inv.total} className="text-xs" />
              </label>
            ))}
            {proposal && proposal.eligible.length === 0 && <p className="px-5 py-6 text-center text-sm text-slate-500">No hay facturas elegibles.</p>}
          </div>
        </div>
        <div className="ts-card overflow-hidden" data-testid="proposal-excluded">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Excluidas con motivo ({proposal?.excluded?.length || 0})</p>
          <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
            {(proposal?.excluded || []).map((x) => (
              <div key={x.invoice.id} className="flex items-center justify-between gap-3 px-5 py-2.5" data-testid={`excluded-${x.invoice.id}`}>
                <span className="text-sm">{x.invoice.invoice_number} — {x.invoice.supplier_name}</span>
                <ToneChip label={x.reason} tone="warning" />
              </div>
            ))}
            {proposal && proposal.excluded.length === 0 && <p className="px-5 py-6 text-center text-sm text-slate-500">Sin exclusiones.</p>}
          </div>
        </div>
      </div>

      <h2 className="ts-section-title mb-3 mt-8">Remesas</h2>
      {batches && batches.length === 0 && <EmptyState title="Sin remesas" description="Crea la primera remesa desde la propuesta de pago." />}
      <div className="space-y-3">
        {(batches || []).map((b) => (
          <div key={b.id}>
            <button
              className={`ts-card ts-card-hover flex w-full flex-wrap items-center justify-between gap-3 p-4 text-left ${openBatch === b.id ? "ring-1 ring-blue-500" : ""}`}
              onClick={() => setParams(openBatch === b.id ? {} : { batch: b.id })}
              data-testid={`batch-row-${b.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-semibold">{b.batch_number}</span>
                <Chip map={BATCH_STATUS} value={b.status} testid={`batch-status-chip-${b.id}`} />
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span>{b.invoice_count} pagos</span>
                <span>pago {fmtDate(b.payment_date)}</span>
                <Money value={b.total_amount} className="text-sm font-bold text-slate-900" />
              </div>
            </button>
            {openBatch === b.id && <BatchDetail bid={b.id} />}
          </div>
        ))}
      </div>
    </div>
  );
}
